import type { CardAvailability, DayName, OffWindow, WorkerPolicy } from './desktop-types';

/** The days in the order people read a week. */
export const WEEK: DayName[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export const DAY_SHORT: Record<DayName, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
};

/** Whether the browser's clock knows the zone (an IANA id); a Windows id or a typo throws a RangeError in Intl. */
export function knownZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/**
 * A moment as the machine's own clock shows it, with the zone's short name: "5:00 PM EDT"; another day leads with it.
 * A zone the browser does not know is shown in UTC rather than breaking the tile and the editor.
 */
export function machineTime(at: string, given: string, now: number = Date.now()): string {
  const zone = knownZone(given) ? given : 'UTC';
  const when = new Date(at);
  const day = (d: Date) => d.toLocaleDateString('en-US', { timeZone: zone });
  const time = when.toLocaleTimeString('en-US', {
    timeZone: zone,
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
  if (day(when) === day(new Date(now))) return time;
  return `${when.toLocaleDateString('en-US', { timeZone: zone, weekday: 'short' })} ${time}`;
}

/** The zone as people say it: "Eastern Time". */
export function zoneWords(zone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      timeZoneName: 'longGeneric',
    }).formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? zone;
  } catch {
    return zone;
  }
}

/** What a card does now: "On", "Off until 5:00 PM EDT (schedule)", "Not used", "Paused". */
export function availabilityWords(
  a: CardAvailability | null,
  zone: string | undefined,
  now?: number,
): string {
  if (!a) return 'On';
  const until = a.until && zone ? ` until ${machineTime(a.until, zone, now)}` : '';
  switch (a.why) {
    case 'paused':
      return 'Paused';
    case 'never':
      return 'Not used';
    case 'off-time':
      return `Off${until} (schedule)`;
    default:
      return `On${until}`;
  }
}

/** Who changed the policy last, and when: "Changed from the web by dbaker@ademero.com at 2:02 PM EDT". */
export function changedWords(p: WorkerPolicy, now?: number): string {
  const at = machineTime(p.changedAt, p.timeZone, now);
  if (p.changedBy.startsWith('web:'))
    return `Changed from the web by ${p.changedBy.slice(4)} at ${at}`;
  return `Changed on the PC at ${at}`;
}

/** An off time in words: "Mon to Fri, 9:00 AM to 5:00 PM"; "Sat, all day". */
export function windowWords(w: OffWindow): string {
  const days = WEEK.filter((d) => w.days.includes(d));
  const run =
    days.length > 2 && WEEK.indexOf(days.at(-1)!) - WEEK.indexOf(days[0]!) === days.length - 1;
  const named = run
    ? `${DAY_SHORT[days[0]!]} to ${DAY_SHORT[days.at(-1)!]}`
    : days.map((d) => DAY_SHORT[d]).join(', ');
  // The same start and end is the whole day (PolicyClock), never a moment.
  if (w.to === w.from) return `${named}, all day`;
  return `${named}, ${clock(w.from)} to ${clock(w.to)}${w.to < w.from ? ' next day' : ''}`;
}

/** "17:00" as "5:00 PM". */
export function clock(hhmm: string): string {
  const [h = 0, m = 0] = hhmm.split(':').map(Number);
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}
